/**
 * Local (non-AI) PII pattern detection for the browser preflight scan.
 *
 * PRODUCT.md "PII Preflight": "The browser also scans raw values locally for
 * obvious email-address and phone-number patterns. Columns matching those
 * patterns are marked as likely PII for user review." This module is the pure,
 * isomorphic core of that scan: per-value pattern matching plus a column-level
 * verdict over a bounded sample, merging value evidence with a header-name hint.
 *
 * All verdicts are ADVISORY — the user has final say in the preflight UI and can
 * mark or unmark any non-Loan/Tax column (PRODUCT.md). The point is to surface
 * obvious PII for review, not to make an irreversible decision.
 *
 * Pure and isomorphic: no DOM, no node:*. Null-like values are recognized via
 * the shared {@link isNullLike} (the SAME set as type inference) — null tokens
 * are NOT redefined here.
 */

import { isNullLike } from './null-like';
import type { LocalPiiSignal } from './pipeline-types';

/**
 * Email pattern: a non-trivial local part, `@`, a domain with at least one dot
 * and a 2+ char TLD. Anchored so the whole trimmed cell must be an email — a
 * cell that merely *contains* an address (e.g. a free-text note) is not flagged,
 * keeping false positives down on description columns.
 */
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/u;

/**
 * Phone pattern: NANP-leaning but permissive (community-bank data is US-centric).
 * Optional `+1` / `1` country prefix, 3-digit area code (optionally parenthesized),
 * then 3 and 4 digit groups, separated by space / dot / hyphen or nothing.
 * Anchored to the whole trimmed cell so a 10-digit account number embedded in
 * free text isn't mistaken for a phone number.
 */
export const PHONE_RE = /^(?:\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/u;

/**
 * US SSN / TIN pattern: 9 digits as `123-45-6789`, `123 45 6789`, or `123456789`.
 * Anchored to the whole trimmed cell. Note the scrub pipeline HMACs the Tax ID
 * *column* regardless; this catches an SSN that leaked into some *other* column
 * so the user can mark it PII.
 */
export const SSN_RE = /^\d{3}[\s-]?\d{2}[\s-]?\d{4}$/u;

/** The PII kinds the local scanner recognizes (excludes the `none` verdict). */
export type PiiKind = 'email' | 'phone' | 'ssn';

/**
 * Classify a single raw cell value. Returns the matched PII kind or `null`.
 * Email is checked before phone/SSN because an email never matches the
 * digit-only patterns, but the ordering keeps intent explicit.
 */
export function scanValueForPii(value: string): PiiKind | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  if (EMAIL_RE.test(trimmed)) return 'email';
  if (PHONE_RE.test(trimmed)) return 'phone';
  if (SSN_RE.test(trimmed)) return 'ssn';
  return null;
}

/** Default fraction of non-null sampled values that must match to flag a column. */
export const DEFAULT_PII_VALUE_THRESHOLD = 0.5;

/**
 * Header-name substrings that strongly suggest a PII column. Lowercased; matched
 * as substrings against the normalized header. Used only as a tie-breaker so a
 * sparse column (few non-null sampled values) with an obvious name is still
 * surfaced; value evidence alone is enough when present.
 */
const PII_HEADER_HINTS: readonly { readonly token: string; readonly kind: PiiKind }[] = [
  { token: 'email', kind: 'email' },
  { token: 'e-mail', kind: 'email' },
  { token: 'phone', kind: 'phone' },
  { token: 'mobile', kind: 'phone' },
  { token: 'cell', kind: 'phone' },
  { token: 'fax', kind: 'phone' },
  { token: 'ssn', kind: 'ssn' },
  { token: 'social security', kind: 'ssn' },
];

function headerHint(headerName: string): PiiKind | null {
  const normalized = headerName.trim().toLowerCase();
  if (normalized === '') return null;
  for (const { token, kind } of PII_HEADER_HINTS) {
    if (normalized.includes(token)) return kind;
  }
  return null;
}

/**
 * Column-level PII verdict over a bounded value sample, optionally informed by
 * the header name.
 *
 * Algorithm:
 *   1. Tally, per PII kind, how many NON-null sampled values match (null-like
 *      values are ignored so a sparse column isn't diluted to a non-match).
 *   2. The dominant kind (most matches) wins when its match ratio over non-null
 *      values is ≥ `threshold`.
 *   3. If no kind clears the value threshold but the HEADER name matches a hint,
 *      flag that kind with `matchRatio: 0` — a name-only signal for the user to
 *      review (covers a column whose sample happened to be all-null).
 *
 * Returns the discriminated {@link LocalPiiSignal}; `{ kind: 'none' }` when
 * neither values nor the name indicate PII.
 */
export function classifyColumnPii(
  sampleValues: readonly string[],
  opts?: { readonly threshold?: number; readonly headerName?: string },
): LocalPiiSignal {
  const threshold = opts?.threshold ?? DEFAULT_PII_VALUE_THRESHOLD;
  const counts: Record<PiiKind, number> = { email: 0, phone: 0, ssn: 0 };
  let nonNull = 0;

  for (const raw of sampleValues) {
    if (isNullLike(raw)) continue;
    nonNull += 1;
    const kind = scanValueForPii(raw);
    if (kind) counts[kind] += 1;
  }

  if (nonNull > 0) {
    // Pick the kind with the most matches; deterministic tie-break by a fixed
    // kind order (email > phone > ssn) so the verdict never depends on Record
    // iteration order.
    const ordered: readonly PiiKind[] = ['email', 'phone', 'ssn'];
    let best: PiiKind | null = null;
    for (const kind of ordered) {
      if (counts[kind] > 0 && (best === null || counts[kind] > counts[best])) {
        best = kind;
      }
    }
    if (best !== null) {
      const matchRatio = counts[best] / nonNull;
      if (matchRatio >= threshold) {
        return { kind: best, matchRatio };
      }
    }
  }

  const hint = opts?.headerName !== undefined ? headerHint(opts.headerName) : null;
  if (hint !== null) {
    return { kind: hint, matchRatio: 0 };
  }

  return { kind: 'none' };
}
